import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { maskCPF, maskTelefone } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Edit, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiResponse, Paciente, PacienteListProps, PaginationInfo } from "./interface/Paciente.interface";



const PacienteList = ({ onEdit, onDelete, refreshTrigger }: PacienteListProps) => {
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  const fetchPacientes = async (page: number = 1, search?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(search && { search })
      });

      const response: { result: ApiResponse } = await api.get(`/api/paciente?${params}`);
      
      if (response.result && response.result.data) {
        setPacientes(response.result.data);
        setPagination(response.result.pagination);
        setCurrentPage(page);
      } else {
        // Fallback para estrutura antiga (sem paginação)
        setPacientes(Array.isArray(response.result) ? response.result : []);
        setPagination(null);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Erro ao carregar pacientes:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os pacientes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPacientes(1);
  }, [refreshTrigger]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== "") {
        fetchPacientes(1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Se não há paginação no backend, usar filtro local
  const filtered = pagination ? pacientes : pacientes.filter((p) => {
    const nome = p.pessoa?.nome?.toLowerCase?.() || "";
    const cpf = (p.pessoa?.pessoaFisica?.cpf || "").toLowerCase();
    const term = searchTerm.toLowerCase();
    return nome.includes(term) || cpf.includes(term);
  });

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Carregando pacientes...</div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou CPF..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Data de Nascimento</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Nenhum paciente encontrado
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((paciente) => (
                <TableRow key={paciente.id}>
                  <TableCell>{paciente.pessoa?.nome || '-'}</TableCell>
                  <TableCell className="font-medium">{paciente.pessoa?.pessoaFisica?.cpf ? maskCPF(paciente.pessoa.pessoaFisica.cpf) : '-'}</TableCell>
                  <TableCell>
                    {paciente.pessoa?.data_nascimento ? new Date(paciente.pessoa.data_nascimento).toLocaleDateString('pt-BR') : '-'}
                  </TableCell>
                  <TableCell>
                    {paciente.pessoa?.telefones && paciente.pessoa.telefones.length > 0
                      ? maskTelefone(paciente.pessoa.telefones[0].telefone)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={paciente.status === true ? "default" : "secondary"}>
                      {paciente.status === true ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(paciente)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(paciente.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Controles de Paginação */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <div className="text-sm text-muted-foreground">
            Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} pacientes
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchPacientes(currentPage - 1, searchTerm || undefined)}
              disabled={!pagination.hasPrev || loading}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchPacientes(currentPage + 1, searchTerm || undefined)}
              disabled={!pagination.hasNext || loading}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default PacienteList;
